// src/services/calibrationApi.ts
import { supabase } from '../../config/supabase';
import { telemetryService } from './telemetryService';

export type CalibrationRow = {
  session_id: string;
  model: string;
  method: 'whole' | 'drive';
  alpha: number;
  r2: number | null;
  created_at: string;
};

export type CalibrationResult = {
  alpha: number;
  method: 'whole' | 'drive' | null;
  updated_at: string | null;
};

export async function getCalibrationForSession(
  sessionId: string
): Promise<CalibrationResult | null> {
  // Resolve to the canonical UUID used across telemetry + calibration
  const resolvedId = await telemetryService.resolveSessionId(sessionId);

  if (!resolvedId) {
    // If we can't resolve the ID, we can't load calibration
    return null;
  }

  // Try view first, fallback to table
  const { data, error } = await supabase
    .from('v_current_calibration')
    .select('session_id, alpha, method, updated_at')
    .eq('session_id', resolvedId)
    .maybeSingle();

  if (error) {
    console.warn(
      '[calibration] v_current_calibration failed; falling back to calibration table',
      error
    );
    const { data: tbl, error: err2 } = await supabase
      .from('calibration')
      .select('session_id, alpha, method, updated_at')
      .eq('session_id', resolvedId)
      .eq('model', 'physics_power_cal_v1')
      .order('updated_at', { ascending: false })
      .limit(1);

    if (err2 || !tbl || tbl.length === 0) return null;
    const row = tbl[0] as any;
    return {
      alpha: row.alpha,
      method: row.method,
      updated_at: row.updated_at,
    };
  }

  if (!data) return null;
  return {
    alpha: data.alpha as number,
    method: data.method as 'whole' | 'drive' | null,
    updated_at: data.updated_at as string | null,
  };
}

// Legacy function for backward compatibility
export async function getCalibration(
  sessionId: string
): Promise<{ alpha: number; method: 'whole' | 'drive' } | null> {
  const result = await getCalibrationForSession(sessionId);
  if (!result || !result.alpha) return null;
  return { alpha: result.alpha, method: result.method as 'whole' | 'drive' };
}
